import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FlightsFilterForm } from 'legacyfrontend/components/filters';
import { useTranslation } from 'react-i18next';

import PackagesApi from 'legacyfrontend/api/paquetes';
import { DATE_KEYS, ROUTE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';

import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import ErrorSnackbar from '../../ErrorSnackbar';

const { BUSCAR_X_VUELOS } = ROUTE_KEYS;

const initialFlightSetter = (params) => {
  const result = {
    origin:
      params?.get('originId') && params?.get('originName')
        ? {
            id:
              params.get('originIdn') && !isNaN(Number(params.get('originIdn')))
                ? Number(params.get('originIdn'))
                : null,
            cityCode: params.get('originId'),
            cityName: params.get('originName').trim(),
          }
        : null,
    destination:
      params?.get('destinationId') && params?.get('destinationName')
        ? {
            id:
              params.get('destinationIdn') && !isNaN(Number(params.get('destinationIdn')))
                ? Number(params.get('destinationIdn'))
                : null,
            cityCode: params.get('destinationId'),
            cityName: params.get('destinationName').trim(),
          }
        : null,
    passengers: params?.get('passengers') ? params.get('passengers').split(',').map(Number) : [30],
    departureDate: params?.get('departureDate') ? new Date(params.get('departureDate')) : null,
    returnDate: params?.get('returnDate') ? new Date(params.get('returnDate')) : null,
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: 'USD',
    isOnlyOneWay: params?.get('isOnlyOneWay') === 'true',
  };

  return result;
};

const FlightsSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { registerOnSearch } = useGlobal();

    const [isUpdatingFlightInfo, setIsUpdatingFlightInfo] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
    const [hasAutoSearched, setHasAutoSearched] = useState(false);
    const [searchTimestamp, setSearchTimestamp] = useState(null);

    const initialFlightFormInput = useMemo(
      () =>
        initialFlightSetter(
          !!search && pathname === BUSCAR_X_VUELOS ? new URLSearchParams(search) : undefined
        ),
      [search, pathname]
    );

    const [flightFormInput, setFlightFormInput] = useState(initialFlightFormInput);

    // Inicializar formulario con parámetros de URL si están presentes
    useEffect(() => {
      if (hasAutoSearched) return;

      if (!flightFormInput) return;

      const hasUrlParams = search && search.length > 1;

      if (hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        const urlParams = new URLSearchParams(search);
        const originId = urlParams.get('originId');
        const originName = urlParams.get('originName');
        const originIdn = urlParams.get('originIdn');
        const destinationId = urlParams.get('destinationId');
        const destinationName = urlParams.get('destinationName');
        const destinationIdn = urlParams.get('destinationIdn');
        const departureDate = urlParams.get('departureDate');
        const returnDate = urlParams.get('returnDate');
        const passengers = urlParams.get('passengers');
        const isOnlyOneWay = urlParams.get('isOnlyOneWay');

        // Solo inicializar si no se ha hecho antes Y si los valores actuales están vacíos o son inválidos
        if (!hasAutoSearched) {
          if (originId && originName) {
            const originObj = {
              id: originIdn && !isNaN(Number(originIdn)) ? Number(originIdn) : null,
              cityCode: originId,
              cityName: originName.trim(),
            };

            setFlightFormInput((prev) => ({
              ...prev,
              origin: originObj,
            }));
          }

          if (
            destinationId &&
            destinationName &&
            (!flightFormInput.destination || !flightFormInput.destination.id)
          ) {
            const destinationObj = {
              id: destinationIdn && !isNaN(Number(destinationIdn)) ? Number(destinationIdn) : null,
              cityCode: destinationId,
              cityName: destinationName.trim(),
            };

            setFlightFormInput((prev) => ({
              ...prev,
              destination: destinationObj,
            }));
          }

          if (departureDate && !flightFormInput.departureDate) {
            setFlightFormInput((prev) => ({
              ...prev,
              departureDate: new Date(departureDate),
            }));
          }

          if (returnDate && !flightFormInput.returnDate) {
            setFlightFormInput((prev) => ({
              ...prev,
              returnDate: new Date(returnDate),
            }));
          }

          if (
            passengers &&
            (!flightFormInput.passengers || flightFormInput.passengers.length === 0)
          ) {
            setFlightFormInput((prev) => ({
              ...prev,
              passengers: passengers.split(',').map(Number),
            }));
          }

          if (isOnlyOneWay && flightFormInput.isOnlyOneWay === undefined) {
            setFlightFormInput((prev) => ({
              ...prev,
              isOnlyOneWay: isOnlyOneWay === 'true',
            }));
          }
        }
      }
    }, [search, pathname, hasAutoSearched, flightFormInput]);

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => {
        // NO abrir automáticamente - solo se abre manualmente
        // Este método se mantiene por compatibilidad pero no hace nada
      },
      getFormInput: () => flightFormInput,
      triggerSearch: () => {
        handleSearchFlightsButton({ preventDefault: () => {} });
      },
      isFormValid: () => {
        return !!(
          flightFormInput.origin &&
          flightFormInput.destination &&
          flightFormInput.departureDate &&
          (flightFormInput.isOnlyOneWay || flightFormInput.returnDate) &&
          flightFormInput.passengers &&
          flightFormInput.passengers.length > 0
        );
      },
    }));

    // NO abrir automáticamente el dialog - solo se abre manualmente

    const updateFlightInfo = useCallback(
      async (origin, destination, currentFlightFormInput) => {
        if (!origin || !destination || isUpdatingFlightInfo) return;

        try {
          setIsUpdatingFlightInfo(true);
          const [originData, destinationData] = await Promise.all([
            PackagesApi.getAirportById(origin.id),
            PackagesApi.getAirportById(destination.id),
          ]);

          if (!originData || !destinationData) {
            throw new Error('No se pudieron obtener los datos de los aeropuertos');
          }

          const isInternational = originData.countryCode !== destinationData.countryCode;
          const flightInfo = {
            originCountryCode: originData.countryCode,
            destinationCountryCode: destinationData.countryCode,
            origin: {
              id: originData.id,
              airportCode: originData.airportCode,
              airportName: originData.airportName,
              cityCode: originData.cityCode,
              cityName: originData.cityName,
              countryCode: originData.countryCode,
            },
            destination: {
              id: destinationData.id,
              airportCode: destinationData.airportCode,
              airportName: destinationData.airportName,
              cityCode: destinationData.cityCode,
              cityName: destinationData.cityName,
              countryCode: destinationData.countryCode,
            },
            departureDate: currentFlightFormInput.departureDate
              ? new Date(currentFlightFormInput.departureDate)
              : null,
            returnDate: currentFlightFormInput.returnDate
              ? new Date(currentFlightFormInput.returnDate)
              : null,
            passengers: currentFlightFormInput.passengers,
            isOnlyOneWay: currentFlightFormInput.isOnlyOneWay,
            isInternational,
          };

          sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
          return flightInfo;
        } catch (error) {
          console.error('Error updating flight info:', error);
          setErrorMessage('Error al actualizar la información del vuelo');
          setOpenToast(true);
        } finally {
          setIsUpdatingFlightInfo(false);
        }
      },
      [isUpdatingFlightInfo]
    );

    const handleFlightOrigin = useCallback(
      async (origin) => {
        if (!origin) return;

        try {
          setFlightFormInput((state) => {
            const newState = { ...state, origin };
            if (state.destination) {
              updateFlightInfo(origin, state.destination, newState).catch((error) =>
                console.error('Error updating flight info:', error)
              );
            }
            return newState;
          });
        } catch (error) {
          console.error('🔍 [FlightsSearch] handleFlightOrigin - Error:', error);
        }
      },
      [updateFlightInfo]
    );

    const handleFlightDestination = useCallback(
      async (destination) => {
        if (!destination) return;

        try {
          setFlightFormInput((state) => {
            const newState = { ...state, destination };
            if (state.origin) {
              updateFlightInfo(state.origin, destination, newState).catch((error) =>
                console.error('Error updating flight info:', error)
              );
            }
            return newState;
          });
        } catch (error) {
          console.error('🔍 [FlightsSearch] handleFlightDestination - Error:', error);
        }
      },
      [updateFlightInfo]
    );

    const handleFlightPassenger = (passengers) => {
      setFlightFormInput((state) => ({ ...state, passengers }));
    };

    const handleFlightsDateRangeSelect = (departureDate, returnDate) => {
      setFlightFormInput({ ...flightFormInput, departureDate, returnDate });
    };

    const handleIsOnlyOneWay = (isOnlyOneWay) => {
      setFlightFormInput({ ...flightFormInput, isOnlyOneWay });
    };

    const handleOccupationChange = (totalTourists) => {};

    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isOriginEmpty, setIsOriginEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isTuristasEmpty, setIsTuristasEmpty] = useState(false);

    // Efecto para limpiar validaciones cuando hay valores seleccionados
    useEffect(() => {
      if (!flightFormInput) return;

      if (flightFormInput.origin) {
        setIsOriginEmpty(false);
      }
      if (flightFormInput.destination) {
        setIsDestinationEmpty(false);
      }
      if (flightFormInput.departureDate) {
        setIsDatesEmpty(false);
      }
      if (flightFormInput.passengers && flightFormInput.passengers.length > 0) {
        setIsTuristasEmpty(false);
      }
    }, [
      flightFormInput?.origin,
      flightFormInput?.destination,
      flightFormInput?.departureDate,
      flightFormInput?.passengers,
    ]);

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const getFlightsQueryString = useCallback(() => {
      const { origin, destination, departureDate, returnDate, passengers, isOnlyOneWay } =
        flightFormInput;
      const flightParams = new URLSearchParams();

      flightParams.set(
        'departureDate',
        formatWithMomentFormat(departureDate, DATE_KEYS.FORMAT.ISO)
      );

      if (returnDate)
        flightParams.set('returnDate', formatWithMomentFormat(returnDate, DATE_KEYS.FORMAT.ISO));

      if (origin) {
        flightParams.set('originId', origin.cityCode);
        flightParams.set('originName', origin.cityName);
        // Solo pasar originIdn si existe y es válido
        if (origin.id && !isNaN(origin.id)) {
          flightParams.set('originIdn', origin.id);
        }
      }

      if (destination) {
        flightParams.set('destinationId', destination.cityCode);
        flightParams.set('destinationName', destination.cityName);
        // Solo pasar destinationIdn si existe y es válido
        if (destination.id && !isNaN(destination.id)) {
          flightParams.set('destinationIdn', destination.id);
        }
      }

      if (passengers) flightParams.set('passengers', passengers.join(','));

      if (isOnlyOneWay) flightParams.set('isOnlyOneWay', true);

      flightParams.set('cache', getCacheStatus());
      flightParams.set('_refresh', Date.now());

      return `?${flightParams.toString()}`;
    }, [flightFormInput]);

    const linkToFLights = useMemo(
      () =>
        flightFormInput?.destination && flightFormInput?.origin
          ? {
              pathname: ROUTE_KEYS.BUSCAR_X_VUELOS,
            }
          : '#',
      [flightFormInput]
    );

    const handleMobileSearchComplete = () => {
      setIsMobileSearchActive(false);
      if (onCloseMobileSearch) {
        onCloseMobileSearch();
      }
      if (onSearchComplete) {
        onSearchComplete();
      }
    };

    const handleSearchFlightsButton = useCallback(
      async (e) => {
        e.preventDefault();

        // Validar que todos los campos requeridos estén presentes y no sean undefined
        if (
          !flightFormInput?.origin ||
          !flightFormInput.origin.cityCode ||
          !flightFormInput.origin.cityName
        ) {
          showError('Por favor seleccione un origen válido');
          return;
        }
        if (
          !flightFormInput?.destination ||
          !flightFormInput.destination.cityCode ||
          !flightFormInput.destination.cityName
        ) {
          showError('Por favor seleccione un destino válido');
          return;
        }
        if (
          !flightFormInput?.departureDate ||
          isNaN(new Date(flightFormInput.departureDate).getTime())
        ) {
          showError('Por favor seleccione una fecha de salida válida');
          return;
        }
        if (
          !flightFormInput?.isOnlyOneWay &&
          (!flightFormInput?.returnDate || isNaN(new Date(flightFormInput.returnDate).getTime()))
        ) {
          showError('Por favor seleccione una fecha de regreso válida');
          return;
        }
        if (
          !flightFormInput?.passengers ||
          !Array.isArray(flightFormInput.passengers) ||
          flightFormInput.passengers.length === 0
        ) {
          setFlightFormInput((prev) => ({ ...prev, passengers: [30] }));
        }

        try {
          // Notificar inicio de búsqueda
          if (onSearchStart) {
            onSearchStart();
          }

          await updateFlightInfo(
            flightFormInput.origin,
            flightFormInput.destination,
            flightFormInput
          );
          setSearchTimestamp(Date.now());

          // En mobile, mostrar loading mientras navegamos
          if (isMobile) {
            // Esperar un poco antes de navegar para mostrar el loading
            setTimeout(() => {
              history.push(linkToFLights.pathname + getFlightsQueryString());
              // Solo registrar la búsqueda después de navegar exitosamente
              registerOnSearch();
              handleMobileSearchComplete();
            }, 500);
          } else {
            history.push(linkToFLights.pathname + getFlightsQueryString());
            // Solo registrar la búsqueda después de navegar exitosamente
            registerOnSearch();
          }
        } catch (error) {
          console.error('🔍 [FlightsSearch] handleSearchFlightsButton - Error:', error);
          showError(error?.message || 'Error al procesar la búsqueda');
          // Notificar fin de búsqueda en caso de error
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkToFLights,
        getFlightsQueryString,
        history,
        registerOnSearch,
        flightFormInput,
        showError,
        updateFlightInfo,
        isMobile,
        handleMobileSearchComplete,
      ]
    );

    const isSearchButtonDisabled = () => {
      return (
        !flightFormInput.origin || !flightFormInput.destination || !flightFormInput.departureDate
      );
    };

    // Búsqueda automática SOLO al recargar la página
    useEffect(() => {
      // Solo ejecutar búsqueda automática si:
      // 1. No se ha ejecutado antes
      // 2. Estamos en la página correcta
      // 3. Hay parámetros en la URL
      // 4. NO estamos en mobile (para evitar que se ejecute al abrir el dialog)
      if (hasAutoSearched || isMobile) return;

      // Verificar que flightFormInput esté inicializado
      if (!flightFormInput) return;

      // Verificar si todos los valores necesarios están presentes para ejecutar la búsqueda
      const hasAllRequiredValues =
        flightFormInput.origin &&
        flightFormInput.destination &&
        flightFormInput.departureDate &&
        flightFormInput.passengers &&
        flightFormInput.passengers.length > 0;

      // Verificar si estamos en la página de búsqueda y hay parámetros en la URL
      const hasUrlParams = search && search.length > 1;

      // Solo ejecutar si tenemos todos los valores y estamos en la página correcta
      if (hasAllRequiredValues && hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        setHasAutoSearched(true);

        try {
          handleSearchFlightsButton({ preventDefault: () => {} });
        } catch (error) {
          console.error('Error en búsqueda automática:', error);
        }
      }
    }, [flightFormInput, search, pathname, hasAutoSearched, isMobile]);

    // Si estamos en mobile, mostrar el formulario completo como base de búsqueda
    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <FlightsFilterForm
                handleOriginSelect={handleFlightOrigin}
                handleDestinationSelect={handleFlightDestination}
                handleDateRangeSelect={handleFlightsDateRangeSelect}
                handleDepartureDateChange={handleFlightsDateRangeSelect}
                handleReturnDateChange={handleFlightsDateRangeSelect}
                handleFlightPassenger={handleFlightPassenger}
                handleOneWayChange={handleIsOnlyOneWay}
                handleOccupationChange={handleFlightPassenger}
                formInput={flightFormInput}
                isOriginEmpty={isOriginEmpty}
                isDestinationEmpty={isDestinationEmpty}
                setIsOriginEmpty={setIsOriginEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isDatesEmpty={false}
                isTuristasEmpty={false}
                setIsDatesEmpty={() => {}}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={false} // Siempre false en móvil para evitar diálogos automáticos
              />
              {/* No mostrar botón en mobile - se maneja en el dialog */}
            </div>
          </div>

          <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
        </>
      );
    }

    if (!isOnTargetPath) return <></>;

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <FlightsFilterForm
              handleOriginSelect={handleFlightOrigin}
              handleDestinationSelect={handleFlightDestination}
              handleDateRangeSelect={handleFlightsDateRangeSelect}
              handleDepartureDateChange={handleFlightsDateRangeSelect}
              handleReturnDateChange={handleFlightsDateRangeSelect}
              handleFlightPassenger={handleFlightPassenger}
              handleOneWayChange={handleIsOnlyOneWay}
              handleOccupationChange={handleFlightPassenger}
              formInput={flightFormInput}
              isOriginEmpty={isOriginEmpty}
              isDestinationEmpty={isDestinationEmpty}
              setIsOriginEmpty={setIsOriginEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isDatesEmpty={false}
              isTuristasEmpty={false}
              setIsDatesEmpty={() => {}}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchFlightsButton}
                disabled={isSearchButtonDisabled()}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                <i className='fa fa-search heading__icon' />
                {t('common.search') || 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default FlightsSearch;
